import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { CaptureResponse, CaptureType } from "@idea-studio/shared";
import { getSummary } from "./api/client";
import { HomeScreen } from "./components/HomeScreen";
import { CaptureScreen } from "./components/CaptureScreen";
import { InsightsScreen } from "./components/InsightsScreen";
import { RunScreen } from "./components/RunScreen";
import { Toast } from "./components/Toast";

type View =
  | { kind: "home" }
  | { kind: "capture"; type: CaptureType }
  | { kind: "insights" }
  | { kind: "run" };

export function App() {
  const [view, setView] = useState<View>({ kind: "home" });
  const [toast, setToast] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ["summary"],
    queryFn: getSummary,
    refetchInterval: 10_000,
  });

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="border-b border-neutral-900">
        <div className="mx-auto flex max-w-xl items-center justify-between px-5 py-4">
          <span className="text-[15px] font-medium tracking-tight text-neutral-100">
            Idea Studio
          </span>
          <StatusBadge
            isFetching={summary.isFetching}
            hasData={Boolean(summary.data)}
            isError={summary.isError}
          />
        </div>
      </header>

      <main className="mx-auto max-w-xl px-5 py-8">
        {view.kind === "home" && (
          <>
            {summary.isError && (
              <ErrorCard
                message={
                  summary.error instanceof Error
                    ? summary.error.message
                    : String(summary.error)
                }
              />
            )}
            <HomeScreen
              onPick={(type) => setView({ kind: "capture", type })}
              onRun={() => setView({ kind: "run" })}
              onInsights={() => setView({ kind: "insights" })}
              focus={summary.data?.current_focus}
              mode={summary.data?.mode}
            />
          </>
        )}

        {view.kind === "capture" && (
          <CaptureScreen
            type={view.type}
            onCancel={() => setView({ kind: "home" })}
            onSuccess={(capture: CaptureResponse) => {
              setView({ kind: "home" });
              const note = capture.warnings.includes("audit_log_partial")
                ? ` (audit-log incompleet)`
                : "";
              setToast(`Opgeslagen als ${capture.capture_id}${note}`);
            }}
          />
        )}

        {view.kind === "insights" &&
          (summary.data ? (
            <InsightsScreen
              summary={summary.data}
              onBack={() => setView({ kind: "home" })}
            />
          ) : (
            <Skeleton onBack={() => setView({ kind: "home" })} label="Inzichten" />
          ))}

        {view.kind === "run" && (
          <RunScreen summary={summary.data} onBack={() => setView({ kind: "home" })} />
        )}
      </main>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}

function StatusBadge({
  isFetching,
  hasData,
  isError,
}: {
  isFetching: boolean;
  hasData: boolean;
  isError: boolean;
}) {
  let dot = "bg-neutral-600";
  let label = "—";
  if (isError) {
    dot = "bg-rose-400";
    label = "offline";
  } else if (isFetching) {
    dot = "bg-amber-300";
    label = "syncing";
  } else if (hasData) {
    dot = "bg-emerald-400";
    label = "live";
  }
  return (
    <span className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-neutral-500">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="mb-6 rounded-xl border border-rose-900/60 bg-rose-950/30 px-4 py-3 text-[13px] text-rose-200">
      <div className="font-medium">State niet bereikbaar</div>
      <div className="mt-1 font-mono text-[11px] opacity-80">{message}</div>
    </div>
  );
}

function Skeleton({ onBack, label }: { onBack: () => void; label: string }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between border-b border-neutral-900 pb-4">
        <button
          type="button"
          onClick={onBack}
          className="text-[14px] text-neutral-400 transition-colors active:text-neutral-200"
        >
          Terug
        </button>
        <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-neutral-500">
          {label}
        </span>
        <span className="w-12" aria-hidden="true" />
      </div>
      <div className="h-32 animate-pulse rounded-2xl bg-neutral-900/60" />
      <div className="h-32 animate-pulse rounded-2xl bg-neutral-900/40" />
    </div>
  );
}
